/**
 * Configuration Utilities
 * 
 * Helper functions to work with unified field configurations and extract
 * specific configurations for different purposes (forms, tables, filters).
 */

/**
 * Extract table columns configuration from unified fields
 * @param {Array} fields - Unified field configuration array
 * @returns {Array} Table columns configuration
 */
export const extractTableColumns = (fields) => {
  return fields
    .filter(field => field.table !== false && field.table)
    .map(field => ({
      key: field.name,
      header: field.table.header || field.label,
      width: field.table.width || 'auto',
      sortable: field.table.sortable !== false,
      displayField: field.table.displayField || field.name,
      cellRenderer: field.table.cellRenderer || 'default'
    }));
};

/**
 * Extract filterable fields configuration from unified fields
 * @param {Array} fields - Unified field configuration array
 * @returns {Array} Filterable fields configuration
 */
export const extractFilterableFields = (fields) => {
  return fields
    .filter(field => field.filterable === true)
    .map(field => ({
      name: field.name,
      label: field.label,
      type: field.filterType || (field.type === 'select' ? 'multiSelect' : 'text'),
      options: field.options || []
    }));
};

/**
 * Extract form fields configuration from unified fields
 * @param {Array} fields - Unified field configuration array
 * @returns {Array} Form fields configuration (excludes hidden fields)
 */
export const extractFormFields = (fields) => {
  return fields.filter(field => field.type !== 'hidden');
};

/**
 * Extract validation rules from unified fields
 * @param {Array} fields - Unified field configuration array
 * @returns {Object} Validation rules object
 */
export const extractValidationRules = (fields) => {
  const rules = {};
  fields.forEach(field => {
    if (field.validation) {
      rules[field.name] = field.validation;
    }
  });
  return rules;
};

/**
 * Get field by name from unified fields
 * @param {Array} fields - Unified field configuration array
 * @param {string} fieldName - Name of the field to find
 * @returns {Object|null} Field configuration or null if not found
 */
export const getFieldByName = (fields, fieldName) => {
  return fields.find(field => field.name === fieldName) || null;
};

/**
 * Get fields by type from unified fields
 * @param {Array} fields - Unified field configuration array
 * @param {string} fieldType - Type of fields to find
 * @returns {Array} Array of fields matching the type
 */
export const getFieldsByType = (fields, fieldType) => {
  return fields.filter(field => field.type === fieldType);
};

/**
 * Get required fields from unified fields
 * @param {Array} fields - Unified field configuration array
 * @returns {Array} Array of required fields
 */
export const getRequiredFields = (fields) => {
  return fields.filter(field => field.required === true);
};

/**
 * Get readonly fields from unified fields
 * @param {Array} fields - Unified field configuration array
 * @returns {Array} Array of readonly fields
 */
export const getReadonlyFields = (fields) => {
  return fields.filter(field => field.readonly === true);
};

/**
 * Get text input fields from unified fields
 * @param {Array} fields - Unified field configuration array
 * @returns {Array} Array of text-based input fields
 */
export const getTextInputFields = (fields) => {
  const textTypes = ['text', 'email', 'password', 'url', 'tel', 'search'];
  return fields.filter(field => textTypes.includes(field.type));
};

/**
 * Get fields with character limits from unified fields
 * @param {Array} fields - Unified field configuration array
 * @returns {Array} Array of fields with maxLength specified
 */
export const getFieldsWithCharacterLimits = (fields) => {
  return fields.filter(field => field.maxLength && field.maxLength > 0);
};

/**
 * Get number input fields from unified fields
 * @param {Array} fields - Unified field configuration array
 * @returns {Array} Array of number type fields
 */
export const getNumberInputFields = (fields) => {
  return fields.filter(field => field.type === 'number');
};

/**
 * Get fields with numeric constraints from unified fields
 * @param {Array} fields - Unified field configuration array
 * @returns {Array} Array of fields with min/max constraints
 */
export const getFieldsWithNumericConstraints = (fields) => {
  return fields.filter(field => 
    field.type === 'number' && (
      field.min !== undefined || 
      field.max !== undefined || 
      field.step !== undefined
    )
  );
};

/**
 * Get currency fields from unified fields
 * @param {Array} fields - Unified field configuration array
 * @returns {Array} Array of fields with currency formatting
 */
export const getCurrencyFields = (fields) => {
  return fields.filter(field => field.currency);
};

/**
 * Get percentage fields from unified fields
 * @param {Array} fields - Unified field configuration array
 * @returns {Array} Array of fields with percentage formatting
 */
export const getPercentageFields = (fields) => {
  return fields.filter(field => field.percentage);
};

/**
 * Convert legacy config to unified config format
 * @param {Object} legacyConfig - Legacy configuration object
 * @returns {Object} Unified configuration object
 */
export const convertLegacyConfig = (legacyConfig) => {
  const { fields, tableColumns, filterableFields, ...rest } = legacyConfig;
  
  // Create unified fields by merging information from all three sources
  const unifiedFields = fields.map(field => {
    const tableColumn = tableColumns?.find(col => col.key === field.name);
    const isFilterable = filterableFields?.includes(field.name);
    
    return {
      ...field,
      // Add table configuration if exists
      table: tableColumn ? {
        header: tableColumn.header,
        width: tableColumn.width,
        sortable: true
      } : false,
      // Add filterable configuration
      filterable: isFilterable,
      filterType: field.type === 'select' ? 'multiSelect' : 
                  field.type === 'date' ? 'dateRange' :
                  field.type === 'number' ? 'numericRange' : 'text'
    };
  });
  
  return {
    ...rest,
    fields: unifiedFields,
    // Add computed getters
    get tableColumns() {
      return extractTableColumns(this.fields);
    },
    get filterableFields() {
      return extractFilterableFields(this.fields);
    },
    get formFields() {
      return extractFormFields(this.fields);
    },
    get validationRules() {
      return extractValidationRules(this.fields);
    }
  };
};

/**
 * Validate unified field configuration
 * @param {Array} fields - Unified field configuration array
 * @returns {Object} Validation result with errors array
 */
export const validateUnifiedFields = (fields) => {
  const errors = [];
  const fieldNames = new Set();
  
  fields.forEach((field, index) => {
    // Check required properties
    if (!field.name) {
      errors.push(`Field at index ${index} is missing 'name' property`);
    }
    
    if (!field.label) {
      errors.push(`Field '${field.name}' is missing 'label' property`);
    }
    
    if (!field.type) {
      errors.push(`Field '${field.name}' is missing 'type' property`);
    }
    
    // Check for duplicate field names
    if (fieldNames.has(field.name)) {
      errors.push(`Duplicate field name '${field.name}' found`);
    }
    fieldNames.add(field.name);
    
    // Validate table configuration
    if (field.table && typeof field.table !== 'boolean' && typeof field.table !== 'object') {
      errors.push(`Field '${field.name}' has invalid 'table' configuration`);
    }
    
    // Validate filter configuration
    if (field.filterable !== undefined && typeof field.filterable !== 'boolean') {
      errors.push(`Field '${field.name}' has invalid 'filterable' configuration`);
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Create a field configuration builder for easier field definition
 */
export class FieldBuilder {
  constructor(name) {
    this.field = { name };
  }
  
  label(label) {
    this.field.label = label;
    return this;
  }
  
  type(type) {
    this.field.type = type;
    return this;
  }
  
  required(required = true) {
    this.field.required = required;
    return this;
  }
  
  readonly(readonly = true) {
    this.field.readonly = readonly;
    return this;
  }
  
  table(config) {
    if (typeof config === 'boolean') {
      this.field.table = config;
    } else {
      this.field.table = { ...config };
    }
    return this;
  }
  
  filterable(filterable = true, filterType = null) {
    this.field.filterable = filterable;
    if (filterType) {
      this.field.filterType = filterType;
    }
    return this;
  }
  
  validation(rules) {
    this.field.validation = rules;
    return this;
  }
  
  options(options) {
    this.field.options = options;
    return this;
  }
  
  lookupEndpoint(endpoint) {
    this.field.lookupEndpoint = endpoint;
    return this;
  }
  
  // Text input specific methods
  maxLength(length) {
    this.field.maxLength = length;
    return this;
  }
  
  minLength(length) {
    this.field.minLength = length;
    return this;
  }
  
  pattern(pattern) {
    this.field.pattern = pattern;
    return this;
  }
  
  placeholder(placeholder) {
    this.field.placeholder = placeholder;
    return this;
  }
  
  autoComplete(value) {
    this.field.autoComplete = value;
    return this;
  }
  
  showCharacterCount(show = true) {
    this.field.showCharacterCount = show;
    return this;
  }
  
  showClearButton(show = true) {
    this.field.showClearButton = show;
    return this;
  }
  
  size(size) {
    this.field.size = size;
    return this;
  }
  
  variant(variant) {
    this.field.variant = variant;
    return this;
  }
  
  prefix(prefix) {
    this.field.prefix = prefix;
    return this;
  }
  
  suffix(suffix) {
    this.field.suffix = suffix;
    return this;
  }
  
  helperText(text) {
    this.field.helperText = text;
    return this;
  }
  
  // Number input specific methods
  min(value) {
    this.field.min = value;
    return this;
  }
  
  max(value) {
    this.field.max = value;
    return this;
  }
  
  step(value) {
    this.field.step = value;
    return this;
  }
  
  decimals(count) {
    this.field.decimals = count;
    return this;
  }
  
  currency(currencyCode) {
    this.field.currency = currencyCode;
    return this;
  }
  
  percentage(isPercentage = true) {
    this.field.percentage = isPercentage;
    return this;
  }
  
  allowNegative(allow = true) {
    this.field.allowNegative = allow;
    return this;
  }
  
  thousandsSeparator(show = true) {
    this.field.thousandsSeparator = show;
    return this;
  }
  
  showSteppers(show = true) {
    this.field.showSteppers = show;
    return this;
  }
  
  range(minValue, maxValue) {
    this.field.min = minValue;
    this.field.max = maxValue;
    return this;
  }
  
  build() {
    return { ...this.field };
  }
}

/**
 * Helper function to create a field builder
 * @param {string} name - Field name
 * @returns {FieldBuilder} Field builder instance
 */
export const field = (name) => new FieldBuilder(name);

const configUtils = {
  extractTableColumns,
  extractFilterableFields,
  extractFormFields,
  extractValidationRules,
  getFieldByName,
  getFieldsByType,
  getRequiredFields,
  getReadonlyFields,
  getTextInputFields,
  getFieldsWithCharacterLimits,
  getNumberInputFields,
  getFieldsWithNumericConstraints,
  getCurrencyFields,
  getPercentageFields,
  convertLegacyConfig,
  validateUnifiedFields,
  FieldBuilder,
  field
};

export default configUtils;
